import React, { useState } from 'react';
import ClientList from './ClientList';
import NewClientForm from './NewClientForm';
import BulkClientUpload from './BulkClientUpload';
import ClientAssignmentModal from './ClientAssignmentModal';
import SearchBar from './SearchBar';
import FilterBar from './FilterBar';

const ClientSection = ({ 
  clients, 
  searchTerm, 
  onSearch, 
  filters, 
  onFilter, 
  onSelectClient, 
  onNewMeeting, 
  onSaveNewClient, 
  onBulkUploadClients, 
  canCreateClientsAndMeetings, 
  onUpdateClient, 
  onDeleteClient,
  onAssignClient,
  permissions,
  currentUser
}) => {
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [showBulkUploadForm, setShowBulkUploadForm] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedClientForAssignment, setSelectedClientForAssignment] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' o 'list'

  // Filtrar y ordenar clientes dentro de ClientSection
  const filteredAndSortedClients = React.useMemo(() => {
    console.log('🔄 ClientSection - Iniciando filtrado de clientes...');
    console.log('👤 Usuario actual:', currentUser?.firstName, currentUser?.lastName, 'Rol:', currentUser?.role);
    console.log('📊 Total de clientes recibidos:', clients.length);
    
    let currentFiltered = [...clients];

    // FILTRAR CLIENTES POR CÉLULAS PARA TODOS LOS ROLES EXCEPTO ADMINISTRADOR (PRIMERO)
    if (currentUser?.role !== 'admin' && currentUser?.cells && currentUser.cells.length > 0) {
      console.log('🔒 Aplicando filtro de células para usuario no administrador...');
      console.log('🏢 Células permitidas para el usuario:', currentUser.cells);
      
      const beforeCellFilter = currentFiltered.length;
      
      currentFiltered = currentFiltered.filter(client => {
        // Verificar si el cliente tiene células asignadas
        let clientCells = [];
        if (client.cells && Array.isArray(client.cells)) {
          clientCells = client.cells;
        } else if (client.cell) {
          // Si cell es un string, dividirlo por comas
          clientCells = client.cell.split(',').map(c => c.trim()).filter(c => c);
        }
        
        const hasAllowedCell = clientCells.some(clientCell => 
          currentUser.cells.includes(clientCell)
        );
        
        if (!hasAllowedCell) {
          console.log(`❌ Cliente filtrado:`, {
            clientId: client.id,
            clientName: client.name,
            clientCells: clientCells,
            userCells: currentUser.cells
          });
        } else {
          console.log(`✅ Cliente permitido:`, {
            clientId: client.id,
            clientName: client.name,
            clientCells: clientCells
          });
        }
        
        return hasAllowedCell;
      });
      
      console.log(`✅ Clientes después del filtro por células: ${currentFiltered.length} (filtrados: ${beforeCellFilter - currentFiltered.length})`);
    } else {
      console.log('👑 Usuario administrador o sin células - sin filtro de células');
    }

    // Aplicar filtro de búsqueda
    if (searchTerm) {
      const beforeSearchFilter = currentFiltered.length;
      currentFiltered = currentFiltered.filter(client =>
        client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (client.specialty && client.specialty.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      console.log(`🔍 Clientes después del filtro de búsqueda: ${currentFiltered.length} (filtrados: ${beforeSearchFilter - currentFiltered.length})`);
    }

    // Aplicar filtros básicos
    if (filters.specialty) {
      currentFiltered = currentFiltered.filter(client => 
        client.specialty === filters.specialty
      );
    }

    if (filters.cell) {
      currentFiltered = currentFiltered.filter(client => {
        // Buscar en el array de células o en el campo cell individual
        if (client.cells && Array.isArray(client.cells)) {
          return client.cells.some(clientCell => 
            clientCell.toLowerCase().includes(filters.cell.toLowerCase())
          );
        }
        if (client.cell) {
          return client.cell.toLowerCase().includes(filters.cell.toLowerCase());
        }
        return false;
      });
    }

    if (filters.month) {
      currentFiltered = currentFiltered.filter(client => 
        client.meetings.some(meeting => {
          const meetingDate = new Date(meeting.date);
          return (meetingDate.getMonth() + 1).toString() === filters.month;
        })
      );
    }

    // Aplicar filtros avanzados
    if (filters.status) {
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
      
      currentFiltered = currentFiltered.filter(client => {
        switch (filters.status) {
          case 'active':
            return client.meetings.some(meeting => new Date(meeting.date) >= thirtyDaysAgo);
          case 'inactive':
            return !client.meetings.some(meeting => new Date(meeting.date) >= thirtyDaysAgo);
          case 'withMeetings':
            return client.meetings.length > 0;
          case 'withoutMeetings':
            return client.meetings.length === 0;
          default:
            return true;
        }
      });
    }

    if (filters.conversionRate) {
      currentFiltered = currentFiltered.filter(client => {
        const totalContacts = client.meetings.reduce((total, m) => total + m.contactsGenerated, 0);
        const totalPatients = client.meetings.reduce((total, m) => total + m.patients, 0);
        const rate = totalContacts > 0 ? (totalPatients / totalContacts) * 100 : 0;
        
        switch (filters.conversionRate) {
          case 'high':
            return rate > 70;
          case 'medium':
            return rate >= 30 && rate <= 70;
          case 'low':
            return rate > 0 && rate < 30;
          case 'zero':
            return rate === 0;
          default:
            return true;
        }
      });
    }

      // Filtro de ejecutivo de ventas
      if (filters.salesExecutive) {
        const normalizedFilter = filters.salesExecutive.trim().toLowerCase();
        const beforeSalesFilter = currentFiltered.length;
        
        currentFiltered = currentFiltered.filter(client => {
          if (!client.salesExecutive) {
            console.log(`❌ Cliente sin ejecutivo: ${client.id} - ${client.name}`);
            return false;
          }

          // Normalizar y limpiar ejecutivos del cliente
          const normalizeName = (name) => {
            return name
              .trim()
              .toLowerCase()
              .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // Remover acentos
              .replace(/\s+/g, ' '); // Eliminar espacios múltiples
          };

          // Obtener todas las variaciones del nombre del ejecutivo
          const userNames = normalizedFilter.split(/\s+/).map(normalizeName);
          
          // Separar ejecutivos del cliente considerando comas y espacios
          const clientExecutives = (
            Array.isArray(client.salesExecutive)
              ? client.salesExecutive
              : client.salesExecutive.split(/[,\/]+/).flatMap(e => e.split(/\s+/))
          ).map(e => normalizeName(e)).filter(e => e);

          // Verificar coincidencia en ambas direcciones para cada parte del nombre
          const match = userNames.some(userPart =>
            clientExecutives.some(clientExec =>
              clientExec.includes(userPart) || userPart.includes(clientExec)
            )
          );

          if (!match) {
            console.log(`❌ Cliente filtrado por ejecutivo:`, {
              clientId: client.id,
              clientName: client.name,
              ejecutivos: clientExecutives,
              filtro: normalizedFilter
            });
          }

          return match;
        });

        console.log(`📈 Clientes después de filtro ejecutivo: ${currentFiltered.length} (filtrados: ${beforeSalesFilter - currentFiltered.length})`);
      }

    if (filters.country) {
      currentFiltered = currentFiltered.filter(client => 
        client.country === filters.country
      );
    }

    // Aplicar filtros de fecha
    if (filters.specificDate) {
      const targetDate = new Date(filters.specificDate);
      currentFiltered = currentFiltered.filter(client =>
        client.meetings.some(meeting => {
          const meetingDate = new Date(meeting.date);
          return meetingDate.toDateString() === targetDate.toDateString();
        })
      );
    }

    if (filters.weekDay) {
      currentFiltered = currentFiltered.filter(client =>
        client.meetings.some(meeting => {
          const meetingDate = new Date(meeting.date);
          return meetingDate.getDay().toString() === filters.weekDay;
        })
      );
    }

    // Aplicar ordenamiento
    currentFiltered.sort((a, b) => {
      let comparison = 0;
      
      switch(filters.sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'specialty':
          comparison = (a.specialty || '').localeCompare(b.specialty || '');
          break;
        case 'conversion':
          const aTotalContacts = a.meetings.reduce((total, m) => total + m.contactsGenerated, 0);
          const aTotalPatients = a.meetings.reduce((total, m) => total + m.patients, 0);
          const aRate = aTotalContacts > 0 ? aTotalPatients / aTotalContacts : 0;
          
          const bTotalContacts = b.meetings.reduce((total, m) => total + m.contactsGenerated, 0);
          const bTotalPatients = b.meetings.reduce((total, m) => total + m.patients, 0);
          const bRate = bTotalContacts > 0 ? bTotalPatients / bTotalContacts : 0;
          
          comparison = aRate - bRate;
          break;
        case 'lastMeeting':
          const aLastMeeting = a.meetings.length > 0 ? Math.max(...a.meetings.map(m => new Date(m.date).getTime())) : 0;
          const bLastMeeting = b.meetings.length > 0 ? Math.max(...b.meetings.map(m => new Date(m.date).getTime())) : 0;
          comparison = aLastMeeting - bLastMeeting;
          break;
        case 'totalMeetings':
          comparison = a.meetings.length - b.meetings.length;
          break;
        case 'totalContacts':
          const aTotalContactsSort = a.meetings.reduce((total, m) => total + m.contactsGenerated, 0);
          const bTotalContactsSort = b.meetings.reduce((total, m) => total + m.contactsGenerated, 0);
          comparison = aTotalContactsSort - bTotalContactsSort;
          break;
        case 'totalPatients':
          const aTotalPatientsSort = a.meetings.reduce((total, m) => total + m.patients, 0);
          const bTotalPatientsSort = b.meetings.reduce((total, m) => total + m.patients, 0);
          comparison = aTotalPatientsSort - bTotalPatientsSort;
          break;
        default:
          comparison = 0;
      }
      
      return filters.sortOrder === 'desc' ? -comparison : comparison;
    });

    return currentFiltered;
  }, [clients, searchTerm, filters]);

  const handleNewClientClick = () => {
    setShowNewClientForm(true);
    setShowBulkUploadForm(false);
  };

  const handleBulkUploadClick = () => {
    setShowBulkUploadForm(true);
    setShowNewClientForm(false);
  };

  const handleCancelForm = () => {
    setShowNewClientForm(false);
    setShowBulkUploadForm(false);
  };

  const handleSaveNewClient = async (clientData) => {
    try {
      await onSaveNewClient(clientData);
      // Después de guardar exitosamente, regresar al dashboard
      setShowNewClientForm(false);
    } catch (error) {
      // Si hay error, mantener el formulario abierto
      console.error('Error al guardar cliente:', error);
    }
  };

  const handleBulkUpload = async (uploadData) => {
    try {
      await onBulkUploadClients(uploadData);
      // Después de subir exitosamente, regresar al dashboard
      setShowBulkUploadForm(false);
    } catch (error) {
      // Si hay error, mantener el formulario abierto
      console.error('Error en carga masiva:', error);
    }
  };

  const handleGeneratePDF = () => {
    // Función para generar PDF con los clientes filtrados
    console.log('Generando PDF con', filteredAndSortedClients.length, 'clientes');
    // Aquí se implementaría la lógica de generación de PDF
    alert(`Generando PDF con ${filteredAndSortedClients.length} clientes`);
  };

  const handleExportExcel = () => {
    // Función para exportar a Excel
    console.log('Exportando a Excel', filteredAndSortedClients.length, 'clientes');
    alert(`Exportando ${filteredAndSortedClients.length} clientes a Excel`);
  };

  const handlePrintList = () => {
    // Función para imprimir la lista
    window.print();
  };

  const handleAssignClient = (client) => {
    setSelectedClientForAssignment(client);
    setShowAssignmentModal(true);
  };

  const handleAssignmentComplete = async (clientId, selectedUsers) => {
    try {
      await onAssignClient(clientId, selectedUsers);
      setShowAssignmentModal(false);
      setSelectedClientForAssignment(null);
    } catch (error) {
      console.error('Error al asignar cliente:', error);
      throw error; // Re-throw para que el modal pueda manejarlo
    }
  };

  const handleCloseAssignmentModal = () => {
    setShowAssignmentModal(false);
    setSelectedClientForAssignment(null);
  };

  // Calcular estadísticas
  const totalClients = clients.length;
  const activeClients = clients.filter(client => client.meetings.length > 0).length;
  const totalMeetings = clients.reduce((total, client) => total + client.meetings.length, 0);
  const uniqueSpecialties = [...new Set(clients.map(client => client.specialty).filter(Boolean))].length;
  const uniqueCells = [...new Set(clients.flatMap(client => client.cells || [client.cell]).filter(Boolean))].length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="responsive-container responsive-padding">
        {/* Header Premium con gradiente - Completamente responsivo */}
        <div className="bg-gradient-to-r from-[#0061A8] to-[#004d8a] rounded-2xl shadow-xl responsive-padding mb-6 sm:mb-8">
          <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center space-y-4 sm:space-y-6 xl:space-y-0">
            <div className="text-white min-w-0 flex-1">
              <h1 className="responsive-text-2xl font-bold mb-2 tracking-tight">
                Gestión de Clientes
              </h1>
              <p className="text-blue-100 responsive-text-base mb-3 sm:mb-4">
                {filteredAndSortedClients.length} de {totalClients} clientes mostrados
              </p>
              <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                <span className="inline-flex items-center px-3 py-1.5 rounded-full responsive-text-sm font-medium bg-white/20 text-white backdrop-blur-sm">
                  <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                  {activeClients} activos
                </span>
                <span className="inline-flex items-center px-3 py-1.5 rounded-full responsive-text-sm font-medium bg-white/20 text-white backdrop-blur-sm">
                  <div className="w-2 h-2 bg-blue-400 rounded-full mr-2"></div>
                  {totalMeetings} juntas totales
                </span>
              </div>
            </div>
            
            {/* Botones de acción principales - Completamente responsivos */}
            <div className="flex flex-wrap gap-2 sm:gap-3 w-full xl:w-auto">
              {/* Botón Generar PDF - Solo para administradores */}
              {permissions.canExportData && (
                <button
                  onClick={handleGeneratePDF}
                  className="group flex items-center space-x-2 bg-white/10 backdrop-blur-sm text-white rounded-xl hover:bg-white/20 transition-all duration-300 border border-white/20 hover:border-white/40 shadow-lg hover:shadow-xl flex-1 sm:flex-none justify-center sm:justify-start px-3 py-2.5 sm:px-6 sm:py-3"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="font-medium responsive-text-sm">
                    <span className="hidden sm:inline">Generar PDF</span>
                    <span className="sm:hidden">PDF</span>
                  </span>
                </button>
              )}

              {/* Botón Nuevo Cliente - Para usuarios con permisos de creación */}
              {permissions.canCreateClients && (
                <button
                  onClick={handleNewClientClick}
                  className="group flex items-center space-x-2 bg-gradient-to-r from-[#A8D08D] to-[#8bc34a] text-white rounded-xl hover:from-[#8bc34a] hover:to-[#A8D08D] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex-1 sm:flex-none justify-center sm:justify-start px-3 py-2.5 sm:px-6 sm:py-3"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span className="font-medium responsive-text-sm">
                    <span className="hidden sm:inline">Nuevo Cliente</span>
                    <span className="sm:hidden">Nuevo</span>
                  </span>
                </button>
              )}

              {/* Botón Carga Masiva - Solo para administradores */}
              {permissions.canBulkUploadClients && (
                <button
                  onClick={handleBulkUploadClick}
                  className="group flex items-center space-x-2 bg-gradient-to-r from-[#63666A] to-[#4a4d52] text-white rounded-xl hover:from-[#4a4d52] hover:to-[#63666A] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex-1 sm:flex-none justify-center sm:justify-start px-3 py-2.5 sm:px-6 sm:py-3"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span className="font-medium responsive-text-sm">
                    <span className="hidden sm:inline">Carga Masiva</span>
                    <span className="sm:hidden">Masiva</span>
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Estadísticas Premium con diseño mejorado */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="group bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-[#0061A8] transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#63666A] font-medium">Total Clientes</p>
                <p className="text-3xl font-bold text-[#0061A8] mt-2">{totalClients}</p>
                <p className="text-xs text-[#63666A] mt-1">{activeClients} clientes activos</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-[#0061A8] to-[#004d8a] rounded-xl">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 0 0 5.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="group bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-[#A8D08D] transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#63666A] font-medium">Total Juntas</p>
                <p className="text-3xl font-bold text-[#A8D08D] mt-2">{totalMeetings}</p>
                <p className="text-xs text-[#63666A] mt-1">{activeClients} clientes con juntas</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-[#A8D08D] to-[#8bc34a] rounded-xl">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="group bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-[#63666A] transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#63666A] font-medium">Especialidades</p>
                <p className="text-3xl font-bold text-[#63666A] mt-2">{uniqueSpecialties}</p>
                <p className="text-xs text-[#63666A] mt-1">especialidades diferentes</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-[#63666A] to-[#4a4d52] rounded-xl">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="group bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-[#D1D3D4] transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#63666A] font-medium">Células</p>
                <p className="text-3xl font-bold text-[#D1D3D4] mt-2">{uniqueCells}</p>
                <p className="text-xs text-[#63666A] mt-1">células diferentes</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-[#D1D3D4] to-[#b8bcc0] rounded-xl">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {showNewClientForm ? (
          <NewClientForm
            onSave={handleSaveNewClient}
            onCancel={handleCancelForm}
          />
        ) : showBulkUploadForm ? (
          <BulkClientUpload
            onUpload={handleBulkUpload}
            onCancel={handleCancelForm}
          />
        ) : (
          <>
            {/* Barra de búsqueda con diseño premium */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <SearchBar onSearch={onSearch} />
            </div>
            
            {/* Filtros avanzados con diseño premium */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <FilterBar onFilter={onFilter} clients={clients} />
            </div>
            
            {/* Barra de herramientas adicionales con diseño premium */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-[#63666A] font-medium">
                    Mostrando {filteredAndSortedClients.length} de {totalClients} clientes
                  </span>
                </div>
                
                <div className="flex items-center space-x-3">
                  {/* Botones de exportación - COMPLETAMENTE REMOVIDOS para usuarios no administradores */}
                  {currentUser && currentUser.role === 'admin' && (
                    <>
                      <button
                        onClick={handleExportExcel}
                        className="group flex items-center space-x-2 text-[#63666A] hover:text-[#0061A8] transition-all duration-300 px-4 py-2 rounded-xl hover:bg-blue-50"
                        title="Exportar a Excel"
                      >
                        <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="hidden sm:inline font-medium">Excel</span>
                      </button>

                      <button
                        onClick={handlePrintList}
                        className="group flex items-center space-x-2 text-[#63666A] hover:text-[#0061A8] transition-all duration-300 px-4 py-2 rounded-xl hover:bg-blue-50"
                        title="Imprimir lista"
                      >
                        <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                        </svg>
                        <span className="hidden sm:inline font-medium">Imprimir</span>
                      </button>
                    </>
                  )}

                  {/* Toggle de vista con diseño premium */}
                  <div className="flex border border-[#D1D3D4] rounded-xl overflow-hidden shadow-sm">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-3 transition-all duration-300 ${
                        viewMode === 'grid' 
                          ? 'bg-gradient-to-r from-[#0061A8] to-[#004d8a] text-white shadow-lg' 
                          : 'text-[#63666A] hover:bg-gray-50'
                      }`}
                      title="Vista en cuadrícula"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-3 transition-all duration-300 ${
                        viewMode === 'list' 
                          ? 'bg-gradient-to-r from-[#0061A8] to-[#004d8a] text-white shadow-lg' 
                          : 'text-[#63666A] hover:bg-gray-50'
                      }`}
                      title="Vista en lista"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Lista de clientes sin contenedor adicional */}
            <ClientList 
              clients={filteredAndSortedClients}
              onSelectClient={onSelectClient}
              onNewMeeting={canCreateClientsAndMeetings ? onNewMeeting : null}
              onUpdateClient={onUpdateClient}
              onDeleteClient={onDeleteClient}
              onAssignClient={handleAssignClient}
              canEdit={canCreateClientsAndMeetings}
              viewMode={viewMode}
              permissions={permissions}
              currentUser={currentUser}
            />
          </>
        )}

        {/* Modal de asignación de clientes */}
        {showAssignmentModal && selectedClientForAssignment && (
          <ClientAssignmentModal
            client={selectedClientForAssignment}
            onAssign={handleAssignmentComplete}
            onClose={handleCloseAssignmentModal}
          />
        )}
      </div>
    </div>
  );
};

export default ClientSection;
